/**
 * Pure helpers shared between HouseholdDetail orchestrator and its
 * sub-components (tabs, dialogs). Kept dependency-free so each helper
 * can be unit-tested in isolation without spinning up React.
 */

export type CategoryItem = {
  id: string;
  name: string;
  icon: string;
  color: string;
  defaultShared: boolean;
  matchPatterns: string[];
  isDefault?: boolean;
  transactionCount?: number;
};

export type SettlementListItem = {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  status: "open" | "awaiting_approval" | "locked";
  invoiceCount: number;
  transactionCount: number;
  totalAmount: string;
  currency: string;
};

export type SettlementYear = {
  year: number;
  settlements: SettlementListItem[];
  total: number;
  currency: string;
};

/**
 * Group settlements by the calendar year of their endDate, descending.
 * Used by SettlementsTab to render `<details>` sections per year with
 * a per-year total badge. Currency is taken from the first settlement
 * in the year — the UI assumes a single currency per household, which
 * the data layer also assumes today.
 */
export function groupSettlementsByYear(items: SettlementListItem[]): SettlementYear[] {
  const map = new Map<number, SettlementListItem[]>();
  for (const s of items) {
    const year = Number.parseInt(s.endDate.slice(0, 4), 10);
    const bucket = map.get(year);
    if (bucket) bucket.push(s);
    else map.set(year, [s]);
  }
  return Array.from(map.entries())
    .map(([year, settlements]) => ({
      year,
      settlements,
      total: settlements.reduce((sum, s) => sum + Number.parseFloat(s.totalAmount), 0),
      // `settlements` is always non-empty here — entries only exist when at
      // least one settlement pushed into them above.
      currency: settlements[0]?.currency ?? "NOK",
    }))
    .sort((a, b) => b.year - a.year);
}

/**
 * Default form values for "create new settlement" — the current calendar
 * month (1st → last day) with a title like "Oktober 2026" matching the
 * locale-formatted month name. The user can edit any field before submit.
 */
export function currentMonthDefaults(): { title: string; startDate: string; endDate: string } {
  const first = new Date();
  first.setDate(1);
  const last = new Date();
  last.setMonth(last.getMonth() + 1, 0);
  const monthName = new Date().toLocaleDateString("nb-NO", { month: "long", year: "numeric" });
  return {
    title: monthName.charAt(0).toUpperCase() + monthName.slice(1),
    startDate: first.toISOString().slice(0, 10),
    endDate: last.toISOString().slice(0, 10),
  };
}

/**
 * Given a YYYY-MM-DD string, return the last day of that same calendar
 * month as YYYY-MM-DD. Used by CreateSettlementDialog to auto-fill the
 * endDate after the user picks a startDate — usually a full-month
 * settlement is the right default. Returns null for unparseable input
 * so callers can fall through to whatever the user typed.
 */
export function lastDayOfMonthIso(yyyyMmDd: string): string | null {
  const [y, m] = yyyyMmDd.split("-").map(Number);
  if (!y || !m || m < 1 || m > 12) return null;
  const d = new Date(y, m, 0);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
